import type { NavItems } from '@ksyos/design-system';
import { Alert } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { CompensationPayoutLevel } from '@common/enums';
import { Loader } from '@frontend/components/elements/common';
import { getSelectedLanguage } from '@frontend/translate';
import type { IEmployment } from '../../interfaces/employment';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { PageTemplate } from '../../templates/PageTemplate';
import { PaymentsNavigation } from '../payments/PaymentsNavigation';
import { PaymentsProfile } from '../payments/PaymentsProfile';
import { PayoutsDetails } from '../payments/PayoutsDetails';

export function PractitionerPayments() {
    const { id: practitionerId, employmentId } = useParams() as { employmentId?: string; id: string };
    const [navigationItems, setNavigationItems] = useState<NavItems[]>([]);
    const [hasValidEmploymentsForPayments, setHasValidEmploymentsForPayments] = useState<boolean>();
    const [selectedEmployment, setSelectedEmployment] = useState<IEmployment>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();

    useEffect(() => {
        loadEmployments();
    }, [practitionerId, employmentId]);

    return (
        <PageTemplate
            sideBarNavigation={renderNavigation()}
            sideBarProfile={renderProfile('large')}
            topBarNavigation={renderNavigation()}
            topBarProfile={renderProfile('small')}
            width="small"
        >
            {isLoading ? (
                <Loader
                    fixedCentered={true}
                    large={true}
                />
            ) : (
                <>
                    {hasValidEmploymentsForPayments ? (
                        selectedEmployment &&
                        selectedEmployment.relationNumber && (
                            <PayoutsDetails
                                backUrl={getBackLink()}
                                payoutLevel={CompensationPayoutLevel.employment}
                                payoutLevelEntityId={selectedEmployment.id}
                                payoutLevelEntityRelationNumber={selectedEmployment.relationNumber}
                            />
                        )
                    ) : (
                        <Alert
                            appearance="warning"
                            hasIcon={true}
                        >
                            {translate.finance.noValidEmploymentsForPayments}
                        </Alert>
                    )}
                </>
            )}
        </PageTemplate>
    );

    function renderProfile(size: 'large' | 'small') {
        return (
            <PaymentsProfile
                practitionerId={practitionerId}
                size={size}
            />
        );
    }

    function renderNavigation() {
        return <PaymentsNavigation navigationItems={navigationItems} />;
    }

    async function loadEmployments() {
        setIsLoading(true);
        const url = '/api/user/employments';
        const result = await abortableFetch.fetchJson(url, 'GET');
        const subItems = result.json.items
            .filter((employment: IEmployment) => employment.relationNumber)
            .map((employment: IEmployment) => {
                if (!employment.organization!.suppressEmployeePayoutOverview) {
                    return {
                        key: employment.id,
                        label: employment.organization!.name,
                        to: `/practitioners/${practitionerId}/payments/${employment.id}`,
                        variant: 'link',
                    };
                }
            })
            .filter((subitem: any) => subitem);
        setHasValidEmploymentsForPayments(subItems.length > 0);

        if (!employmentId && subItems.length > 0) {
            const firstEmployment = result.json.items.find(
                (employment: IEmployment) => employment.id === subItems[0].key
            );
            setSelectedEmployment(firstEmployment);

            window.history.replaceState(null, '', `/practitioners/${practitionerId}/payments/${firstEmployment.id}`);
        }

        if (employmentId) {
            setSelectedEmployment(result.json.items.find((employment: IEmployment) => employment.id === employmentId));
        }

        const items: NavItems[] = [
            {
                items: subItems,
                key: 'payments',
                label: translate.finance.payments,
                to: `/practitioners/${practitionerId}/payments`,
                variant: 'link',
            },
        ];
        setNavigationItems(items);
        setIsLoading(false);
    }

    function getBackLink() {
        let backLink = `/practitioners/${practitionerId}/payments`;
        if (selectedEmployment?.id) {
            backLink += `/${selectedEmployment.id}`;
        }

        return backLink;
    }
}
